import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { Routes, Route, Link } from "react-router-dom";
import { 
  ChevronDown, 
  Globe, 
  Languages, 
  Shield, 
  FileCheck, 
  ArrowRight,
  Volume2,
  VolumeX,
  CheckCircle,
  Send,
  Upload,
  Menu,
  X,
  Mail,
  Phone,
  Layers,
  Settings,
  Linkedin,
  Sparkles,
  ExternalLink
} from "lucide-react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import PrivacyPolicy from "@/PrivacyPolicy";
import CareersPage, { JobDetailPage } from "@/CareersPage";
import AdminPage from "@/AdminPage";
import TermsOfService from "@/TermsOfService";
import AboutPage from "@/AboutPage";
import PortfolioPage from "@/PortfolioPage";
import ApplyPage from "@/ApplyPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Floating Subtitle Component
const FloatingSubtitle = ({ text, delay, position }) => (
  <motion.div
    className="absolute mono-font text-sm md:text-base"
    style={{ ...position }}
    initial={{ opacity: 0, y: 20 }}
    animate={{ 
      opacity: [0, 0.5, 0.5, 0],
      y: [20, 0, 0, -20]
    }}
    transition={{ 
      duration: 6,
      delay,
      repeat: Infinity,
      repeatDelay: 2
    }}
  >
    <span className="text-[#17B352]">[</span>
    <span className="text-[#A1A1AA]">{text}</span>
    <span className="text-[#17B352]">]</span>
  </motion.div>
);

// Hero Section
const HeroSection = ({ onWorkWithUs, onJoinTranslator }) => {
  const subtitles = [
    { text: "English → Hindi", delay: 0, position: { top: '15%', left: '10%' } },
    { text: "English → Spanish", delay: 2, position: { top: '25%', right: '15%' } },
    { text: "Japanese → English", delay: 4, position: { bottom: '30%', left: '20%' } },
    { text: "Korean → Tamil", delay: 1, position: { top: '40%', right: '8%' } },
    { text: "French → Arabic", delay: 3, position: { bottom: '20%', right: '25%' } },
    { text: "German → Portuguese", delay: 5, position: { top: '60%', left: '5%' } },
  ];

  return (
    <section 
      data-testid="hero-section" 
      className="relative min-h-screen flex items-center justify-center hero-gradient overflow-hidden"
    >
      {/* Film grain overlay */}
      <div className="film-grain"></div>
      
      {/* Floating subtitles */}
      {subtitles.map((sub, i) => (
        <FloatingSubtitle key={i} {...sub} />
      ))}
      
      {/* Main content */}
      <div className="relative z-10 text-center px-5 max-w-5xl mx-auto">
        <motion.h1 
          className="text-5xl md:text-7xl lg:text-[6.5rem] tracking-tight mb-6 leading-[1] italic"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          We Translate Stories.
          <br />
          <span className="glow-text text-[#17B352]">The World Watches.</span>
        </motion.h1>

        <motion.p
          className="mono-font text-xs md:text-sm tracking-[0.25em] uppercase mb-8 md:mb-12 inline-block highlight-marker"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="relative z-10 px-3 py-1 text-[#0B0B0D]">Subtitling & Localization for Global Media Platforms</span>
        </motion.p>
        
        <motion.div 
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <button 
            data-testid="work-with-us-btn"
            onClick={onWorkWithUs}
            className="glow-button"
          >
            Start a Project
          </button>
          <button 
            data-testid="join-translator-btn"
            onClick={onJoinTranslator}
            className="secondary-button"
          >
            Join as Translator
          </button>
        </motion.div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <ChevronDown className="w-8 h-8 text-[#52525B]" />
      </motion.div>
    </section>
  );
};

// Global Reach Section
const GlobalReachSection = () => {
  const stats = [
    { number: "50+", label: "Languages" },
    { number: "10,489+", label: "Projects Delivered" },
    { number: "24/7", label: "Operations" }
  ];

  const regions = [
    { name: "USA", x: 20, y: 18 },
    { name: "Europe", x: 48, y: 14 },
    { name: "Middle East", x: 58, y: 22 },
    { name: "India", x: 66, y: 24 }
  ];

  return (
    <section data-testid="global-reach-section" className="py-16 md:py-24 lg:py-32 relative">
      <div className="max-w-7xl mx-auto px-5 md:px-6">
        {/* Capability strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-20 md:mb-28"
        >
          <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] text-center mb-8 uppercase">
            Built for Modern Media Localization Workflows
          </p>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {["OTT Localization", "QC & Compliance", "Multi-language Delivery", "Human + AI Workflows"].map((cap, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.1, duration: 0.5 }}
                className="mono-font text-[11px] md:text-xs px-4 md:px-5 py-2.5 md:py-3 rounded-full border border-[rgba(255,255,255,0.08)] text-[#A1A1AA] bg-[rgba(255,255,255,0.02)] uppercase tracking-wider"
              >
                {cap}
              </motion.span>
            ))}
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] mb-4 uppercase">Global Presence</p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight">Reaching Every Screen</h2>
        </motion.div>

        {/* Minimal World Map */}
        <motion.div 
          className="relative w-full max-w-4xl mx-auto aspect-[2/1] mb-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <svg viewBox="0 0 100 50" className="w-full h-full">
            {/* Subtle grid */}
            <line x1="0" y1="25" x2="100" y2="25" stroke="#1a1a1c" strokeWidth="0.15"/>
            <line x1="50" y1="0" x2="50" y2="50" stroke="#1a1a1c" strokeWidth="0.15"/>
            <line x1="0" y1="12.5" x2="100" y2="12.5" stroke="#1a1a1c" strokeWidth="0.08"/>
            <line x1="0" y1="37.5" x2="100" y2="37.5" stroke="#1a1a1c" strokeWidth="0.08"/>
            <line x1="25" y1="0" x2="25" y2="50" stroke="#1a1a1c" strokeWidth="0.08"/>
            <line x1="75" y1="0" x2="75" y2="50" stroke="#1a1a1c" strokeWidth="0.08"/>

            {/* Connection lines from center hub to each region */}
            {regions.map((region, i) => (
              <line 
                key={`line-${i}`}
                x1="50" y1="25" 
                x2={region.x} y2={region.y} 
                stroke="rgba(23, 179, 82, 0.2)" 
                strokeWidth="0.2"
                strokeDasharray="0.8,0.6"
              />
            ))}
            
            {/* Region dots — small and precise */}
            {regions.map((region, i) => (
              <g key={`dot-${i}`}>
                <circle 
                  cx={region.x} 
                  cy={region.y} 
                  r="1.2" 
                  fill="rgba(23, 179, 82, 0.2)"
                  className="pulse-dot"
                  style={{ animationDelay: `${i * 0.5}s` }}
                />
                <circle cx={region.x} cy={region.y} r="0.5" fill="#17B352"/>
              </g>
            ))}
            
            {/* Center hub */}
            <circle cx="50" cy="25" r="0.7" fill="#007AFF"/>
            <circle cx="50" cy="25" r="1.4" fill="rgba(0, 122, 255, 0.12)"/>
          </svg>
          
          {/* Region labels */}
          {regions.map((region, i) => (
            <motion.div
              key={i}
              className="absolute mono-font text-[10px] text-[#52525B]"
              style={{ 
                left: `${region.x}%`, 
                top: `${region.y}%`,
                transform: 'translate(-50%, -150%)'
              }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 + i * 0.2 }}
            >
              {region.name}
            </motion.div>
          ))}
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 md:gap-8">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.2, duration: 0.6 }}
            >
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#17B352] mb-1 md:mb-2 italic" style={{ fontFamily: "'Instrument Serif', serif" }}>{stat.number}</div>
              <div className="mono-font text-[10px] sm:text-xs text-[#A1A1AA] tracking-[0.15em] sm:tracking-[0.2em] uppercase">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Languages,
      title: "Subtitling & Closed Captions",
      description: "Frame-accurate subtitles and SDH/CC for global audiences across all streaming platforms."
    },
    {
      icon: FileCheck,
      title: "Dubbing Script Adaptation",
      description: "Cultural and linguistic adaptation for lip-sync dubbing and voice-over productions."
    },
    {
      icon: Shield,
      title: "QC & Compliance",
      description: "Netflix, Disney+, Amazon Prime compliance. Industry-standard quality control workflows."
    },
    {
      icon: Globe,
      title: "Multi-language Management",
      description: "End-to-end localization management for 50+ language combinations at scale."
    }
  ];

  return (
    <section data-testid="services-section" className="py-16 md:py-24 lg:py-32 bg-[#0a0a0c]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] mb-4 uppercase">What We Do</p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight">Our Services</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, i) => (
            <motion.div
              key={i}
              className="glass-card p-8 rounded-xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
            >
              <service.icon className="w-10 h-10 text-[#52525B] mb-6 service-icon" />
              <h3 className="text-2xl mb-3">{service.title}</h3>
              <p className="text-[#A1A1AA] leading-relaxed">{service.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Integrated Pillars */}
        <div className="mt-20 md:mt-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-10"
          >
            <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] mb-3 uppercase">Integrated Pillars</p>
            <h3 className="text-3xl md:text-4xl">Core Services</h3>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: Layers, title: "Subtitle Localization", items: ["Translation & Adaptation", "SDH / Closed Captions", "Subtitle QC", "Spotting & Timing", "Conforms & Reformatting", "Template Creation"] },
              { icon: FileCheck, title: "English Master Templates", items: ["Dialogue Accuracy", "Speaker Identification", "Grammar & Formatting", "Timing Optimization", "Reading Speed Compliance", "Platform Compliance"] },
              { icon: Settings, title: "Media Operations Support", items: ["Vendor Management", "Localization Coordination", "Workflow Tracking", "Resource Management", "Metadata Handling", "Delivery Operations"] }
            ].map((pillar, i) => (
              <motion.div
                key={i}
                className="glass-card rounded-xl p-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.6 }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[rgba(23,179,82,0.08)]">
                    <pillar.icon className="w-4 h-4 text-[#17B352]" />
                  </div>
                  <h4 className="text-lg">{pillar.title}</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {pillar.items.map((item, j) => (
                    <span key={j} className="mono-font text-[11px] px-2.5 py-1 rounded-full border border-[rgba(255,255,255,0.07)] text-[#A1A1AA] bg-[rgba(255,255,255,0.02)]">
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <Link to="/about#services" className="secondary-button inline-flex items-center gap-2">
            Know more about our core services <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Workflow Section
const WorkflowSection = () => {
  const steps = [
    { number: "01", title: "Receive", description: "Asset ingestion" },
    { number: "02", title: "Translate", description: "Expert linguists" },
    { number: "03", title: "QC", description: "Quality review" },
    { number: "04", title: "Deliver", description: "Platform ready" }
  ];

  return (
    <section data-testid="workflow-section" className="py-16 md:py-24 lg:py-32 relative overflow-hidden">
      {/* Animated waveform background */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 1200 200" preserveAspectRatio="none">
          <motion.path
            d="M0,100 Q300,50 600,100 T1200,100"
            fill="none"
            stroke="#17B352"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 2, ease: "easeInOut" }}
          />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] mb-4 uppercase">Process</p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight">How We Work</h2>
        </motion.div>

        {/* Workflow Timeline */}
        <div className="relative">
          {/* Connector line */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#17B352]/30 to-transparent transform -translate-y-1/2" />
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                className="workflow-step text-center relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2, duration: 0.6 }}
              >
                <div className="step-icon w-20 h-20 mx-auto mb-6 rounded-full bg-[#121214] border border-[#1f1f23] flex items-center justify-center transition-all duration-300">
                  <span className="mono-font text-2xl text-[#17B352]">{step.number}</span>
                </div>
                <h3 className="text-xl mb-2">{step.title}</h3>
                <p className="mono-font text-xs text-[#52525B] tracking-[0.15em] uppercase">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Why Graphikpro Section
const WhySection = () => {
  const features = [
    "Industry-standard formatting",
    "Tight deadline delivery",
    "Multi-language scale",
    "Studio-grade QC",
    "Dedicated project teams"
  ];

  return (
    <section data-testid="why-section" className="py-16 md:py-24 lg:py-32 bg-[#0a0a0c]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Typography */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] mb-4 uppercase">Why Choose Us</p>
            <h2 className="text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Precision
              <br />
              <span className="text-[#17B352]">In Every</span>
              <br />
              Frame.
            </h2>
          </motion.div>

          {/* Right - Features */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            {features.map((feature, i) => (
              <motion.div
                key={i}
                className="flex items-center gap-4 group"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.1 }}
              >
                <div className="w-8 h-8 rounded-full bg-[#121214] border border-[#1f1f23] flex items-center justify-center group-hover:border-[#17B352] transition-colors">
                  <CheckCircle className="w-4 h-4 text-[#17B352]" />
                </div>
                <span className="text-lg text-[#A1A1AA] group-hover:text-[#E5E5E5] transition-colors animated-underline">
                  {feature}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Translator Network Section
const TranslatorSection = ({ id }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    language_pair: '',
    experience: '',
    cv_url: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/translator-application`, formData);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
      setFormData({ name: '', email: '', language_pair: '', experience: '', cv_url: '' });
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id={id} data-testid="translator-section" className="py-16 md:py-24 lg:py-32 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="aspect-[4/3] rounded-xl overflow-hidden">
              <img 
                src="https://images.pexels.com/photos/2739306/pexels-photo-2739306.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Translator working"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0D] via-transparent to-transparent" />
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] mb-4 uppercase">Join Our Network</p>
            <h2 className="text-4xl md:text-5xl mb-3 tracking-tight">Built by Translators.</h2>
            <p className="text-[#A1A1AA] mb-8 text-lg">Designed for Global Media.</p>

            {submitted ? (
              <motion.div 
                className="glass-card p-8 rounded-xl text-center success-message"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <CheckCircle className="w-16 h-16 text-[#32D74B] mx-auto mb-4" />
                <h3 className="text-2xl mb-2">Application Received!</h3>
                <p className="text-[#A1A1AA]">We'll review your profile and get back to you soon.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="glass-card p-8 rounded-xl space-y-5">
                <div>
                  <label className="mono-font text-xs text-[#52525B] mb-2 block uppercase tracking-wider">Name</label>
                  <input
                    data-testid="translator-name-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="glass-input"
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div>
                  <label className="mono-font text-xs text-[#52525B] mb-2 block uppercase tracking-wider">Email</label>
                  <input
                    data-testid="translator-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="glass-input"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="mono-font text-xs text-[#52525B] mb-2 block uppercase tracking-wider">Language Pair</label>
                  <input
                    data-testid="translator-language-input"
                    type="text"
                    value={formData.language_pair}
                    onChange={(e) => setFormData({...formData, language_pair: e.target.value})}
                    className="glass-input"
                    placeholder="e.g., English → Hindi"
                    required
                  />
                </div>
                <div>
                  <label className="mono-font text-xs text-[#52525B] mb-2 block uppercase tracking-wider">Experience</label>
                  <textarea
                    data-testid="translator-experience-input"
                    value={formData.experience}
                    onChange={(e) => setFormData({...formData, experience: e.target.value})}
                    className="glass-textarea"
                    placeholder="Brief description of your experience..."
                    required
                  />
                </div>
                <div>
                  <label className="mono-font text-xs text-[#52525B] mb-2 block uppercase tracking-wider">CV/Portfolio Link (Optional)</label>
                  <input
                    data-testid="translator-cv-input"
                    type="url"
                    value={formData.cv_url}
                    onChange={(e) => setFormData({...formData, cv_url: e.target.value})}
                    className="glass-input"
                    placeholder="https://..."
                  />
                </div>
                <button
                  data-testid="translator-submit-btn"
                  type="submit"
                  disabled={isSubmitting}
                  className="glow-button w-full flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? "Submitting..." : (
                    <>
                      <Send className="w-4 h-4" />
                      Apply Now
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Founder Quote Section
const FounderSection = () => (
  <section data-testid="founder-section" className="py-16 md:py-24 lg:py-32 bg-[#0a0a0c]">
    <div className="max-w-4xl mx-auto px-6 text-center">
      <motion.div
        className="quote-container"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      >
        <p className="text-3xl md:text-4xl lg:text-5xl text-[#E5E5E5] leading-relaxed mb-8 display-font italic">
          "Content travels.
          <br />
          <span className="text-[#17B352]">Meaning must survive."</span>
        </p>
        <div className="flex items-center justify-center gap-4">
          <div className="w-12 h-px bg-[#17B352]" />
          <p className="mono-font text-sm text-[#52525B] tracking-widest uppercase">
            — Maddy, Founder
          </p>
          <div className="w-12 h-px bg-[#17B352]" />
        </div>
      </motion.div>
    </div>
  </section>
);

// Contact Form Section
const ContactSection = ({ id }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error(error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id={id} data-testid="contact-section" className="py-16 md:py-24 lg:py-32 relative cta-glow">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] mb-4 uppercase">Get Started</p>
          <h2 className="text-5xl md:text-6xl lg:text-7xl mb-6 tracking-tight leading-[0.95]">
            Ready to Localize
            <br />
            <span className="text-[#17B352]">At Scale?</span>
          </h2>
        </motion.div>

        {submitted ? (
          <motion.div 
            className="glass-card p-8 rounded-xl text-center success-message max-w-md mx-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <CheckCircle className="w-16 h-16 text-[#32D74B] mx-auto mb-4" />
            <h3 className="text-2xl mb-2">Message Sent!</h3>
            <p className="text-[#A1A1AA]">We'll get back to you within 24 hours.</p>
          </motion.div>
        ) : (
          <motion.form 
            onSubmit={handleSubmit}
            className="glass-card p-8 rounded-xl space-y-5 text-left max-w-xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="mono-font text-xs text-[#52525B] mb-2 block uppercase tracking-wider">Name</label>
                <input
                  data-testid="contact-name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="glass-input"
                  placeholder="Your name"
                  required
                />
              </div>
              <div>
                <label className="mono-font text-xs text-[#52525B] mb-2 block uppercase tracking-wider">Email</label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="glass-input"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>
            <div>
              <label className="mono-font text-xs text-[#52525B] mb-2 block uppercase tracking-wider">Company (Optional)</label>
              <input
                data-testid="contact-company-input"
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                className="glass-input"
                placeholder="Your company"
              />
            </div>
            <div>
              <label className="mono-font text-xs text-[#52525B] mb-2 block uppercase tracking-wider">Message</label>
              <textarea
                data-testid="contact-message-input"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="glass-textarea"
                placeholder="Tell us about your project..."
                required
              />
            </div>
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={isSubmitting}
              className="glow-button w-full flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? "Sending..." : (
                <>
                  Start a Project
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </motion.form>
        )}

        {/* Direct Contact Info */}
        <motion.div 
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="mono-font text-[10px] text-[#52525B] tracking-[0.2em] uppercase">Or reach us directly</div>
          <a href="mailto:askus@graphikpro.com" className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#17B352] transition-colors">
            <Mail className="w-4 h-4" />
            <span className="text-sm">askus@graphikpro.com</span>
          </a>
          <a href="https://wa.me/919342055964" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#17B352] transition-colors">
            <Phone className="w-4 h-4" />
            <span className="text-sm">+91 93420 55964</span>
          </a>
          <a href="https://www.linkedin.com/in/madhiee/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#17B352] transition-colors">
            <Linkedin className="w-4 h-4" />
            <span className="text-sm">LinkedIn</span>
          </a>
        </motion.div>

        {/* Sister-brand cross-sell */}
        <motion.div
          className="mt-8 flex justify-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <a
            data-testid="graphikpro-company-cta"
            href="https://graphikpro.company"
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-center gap-2 mono-font text-xs uppercase tracking-[0.2em] px-5 py-3 rounded-full border border-[#17B352]/40 text-[#17B352] hover:bg-[#17B352] hover:text-black transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Visit graphikpro.company
            <ExternalLink className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-opacity" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Audio Toggle Component - Random rotation across uploaded clips (up to 3), with synth fallback
const AudioToggle = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioSettings, setAudioSettings] = useState(null);
  const [showTeaser, setShowTeaser] = useState(false);
  const [nowPlayingLabel, setNowPlayingLabel] = useState("");
  const audioCtxRef = useRef(null);
  const nodesRef = useRef([]);
  const elemRef = useRef(null);
  const lastIndexRef = useRef(-1);

  useEffect(() => {
    axios.get(`${API}/site-audio`)
      .then((r) => setAudioSettings(r.data || null))
      .catch(() => setAudioSettings(null));
  }, []);

  // Landing teaser — show the mini-player briefly so visitors notice audio is available
  useEffect(() => {
    const dismissed = sessionStorage.getItem("audio_teaser_seen");
    if (dismissed) return;
    const showTimer = setTimeout(() => setShowTeaser(true), 1200);
    const hideTimer = setTimeout(() => {
      setShowTeaser(false);
      sessionStorage.setItem("audio_teaser_seen", "1");
    }, 7800);
    return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
  }, []);

  const tracks = (audioSettings?.tracks) || [];
  const audioOn = audioSettings?.enabled !== false;
  const fileVolume = Math.max(0, Math.min(1, audioSettings?.volume ?? 0.18));

  const pickRandomTrackIndex = () => {
    if (tracks.length === 0) return -1;
    if (tracks.length === 1) return 0;
    let next = Math.floor(Math.random() * tracks.length);
    if (next === lastIndexRef.current) {
      next = (next + 1) % tracks.length;
    }
    return next;
  };

  const playNextRandom = async () => {
    const idx = pickRandomTrackIndex();
    if (idx < 0) return false;
    lastIndexRef.current = idx;
    const t = tracks[idx];
    const src = t.url.startsWith("http") ? t.url : `${process.env.REACT_APP_BACKEND_URL}${t.url}`;
    if (!elemRef.current) {
      const el = new Audio();
      el.preload = "auto";
      el.addEventListener("ended", () => { playNextRandom(); });
      elemRef.current = el;
    }
    elemRef.current.src = src;
    elemRef.current.volume = fileVolume;
    setNowPlayingLabel(t.original_filename || t.filename);
    try {
      await elemRef.current.play();
      return true;
    } catch (e) {
      return false;
    }
  };

  const createAmbientDrone = () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;
    const master = ctx.createGain();
    master.gain.value = 0.06;
    master.connect(ctx.destination);
    const bass = ctx.createOscillator(); bass.type = 'sine'; bass.frequency.value = 55;
    const bassGain = ctx.createGain(); bassGain.gain.value = 0.5;
    const bassFilter = ctx.createBiquadFilter(); bassFilter.type = 'lowpass'; bassFilter.frequency.value = 120;
    bass.connect(bassFilter); bassFilter.connect(bassGain); bassGain.connect(master); bass.start();
    const pad = ctx.createOscillator(); pad.type = 'triangle'; pad.frequency.value = 110;
    const padGain = ctx.createGain(); padGain.gain.value = 0.2;
    const padFilter = ctx.createBiquadFilter(); padFilter.type = 'lowpass'; padFilter.frequency.value = 300;
    pad.connect(padFilter); padFilter.connect(padGain); padGain.connect(master); pad.start();
    const shimmer = ctx.createOscillator(); shimmer.type = 'sine'; shimmer.frequency.value = 220;
    const shimmerGain = ctx.createGain(); shimmerGain.gain.value = 0.08;
    shimmer.connect(shimmerGain); shimmerGain.connect(master); shimmer.start();
    const lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = 0.15;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 8;
    lfo.connect(lfoGain); lfoGain.connect(pad.frequency); lfo.start();
    nodesRef.current = [bass, pad, shimmer, lfo];
    setNowPlayingLabel("Ambient drone");
  };

  const stopAll = () => {
    if (elemRef.current) { try { elemRef.current.pause(); elemRef.current.src = ""; } catch(e) {} }
    nodesRef.current.forEach(n => { try { n.stop(); } catch(e) {} });
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch(e) {} }
    audioCtxRef.current = null;
    nodesRef.current = [];
    setNowPlayingLabel("");
  };

  const startAudio = async () => {
    if (audioOn && tracks.length > 0) {
      const ok = await playNextRandom();
      if (ok) return;
      // playback failed (e.g. format), fall through to synth
      elemRef.current = null;
    }
    createAmbientDrone();
  };

  const toggleAudio = async () => {
    // Dismiss teaser on interaction
    setShowTeaser(false);
    sessionStorage.setItem("audio_teaser_seen", "1");
    if (isPlaying) {
      stopAll();
      setIsPlaying(false);
    } else {
      await startAudio();
      setIsPlaying(true);
    }
  };

  useEffect(() => { return () => stopAll(); }, []);

  return (
    <div className="relative">
      {/* Landing mini-player teaser */}
      {showTeaser && (
        <div
          data-testid="audio-teaser"
          className="fixed right-8 bottom-[5.5rem] z-[101] animate-fade-in"
        >
          <button
            onClick={toggleAudio}
            className="group flex items-center gap-3 pl-3 pr-4 py-2.5 rounded-full bg-[#111114] border border-[#17B352]/30 shadow-2xl shadow-[#17B352]/20 hover:border-[#17B352] transition-colors whitespace-nowrap"
          >
            <div className="flex items-center gap-0.5 h-4">
              <div className="sound-bar animate-pulse" style={{ height: '6px', background: '#17B352' }} />
              <div className="sound-bar animate-pulse" style={{ height: '14px', background: '#17B352', animationDelay: '0.15s' }} />
              <div className="sound-bar animate-pulse" style={{ height: '10px', background: '#17B352', animationDelay: '0.3s' }} />
            </div>
            <div className="text-left">
              <p className="mono-font text-[9px] tracking-[0.25em] text-[#17B352] uppercase">Tap to play</p>
              <p className="text-xs text-white">Background score</p>
            </div>
            <span
              data-testid="audio-teaser-close"
              role="button"
              aria-label="Dismiss"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setShowTeaser(false);
                sessionStorage.setItem("audio_teaser_seen", "1");
              }}
              className="text-[#52525B] hover:text-white text-xs leading-none ml-1 cursor-pointer"
            >×</span>
          </button>
        </div>
      )}
      <button
        data-testid="audio-toggle"
        onClick={toggleAudio}
        className={`audio-toggle ${isPlaying ? 'playing' : ''}`}
        aria-label={isPlaying ? "Mute audio" : "Play audio"}
        title={nowPlayingLabel || undefined}
      >
        {isPlaying ? (
          <div className="flex items-center gap-0.5 h-4">
            <div className="sound-bar" style={{ height: '8px' }} />
            <div className="sound-bar" style={{ height: '16px' }} />
            <div className="sound-bar" style={{ height: '12px' }} />
            <div className="sound-bar" style={{ height: '8px' }} />
          </div>
        ) : (
          <VolumeX className="w-5 h-5 text-[#52525B]" />
        )}
      </button>
    </div>
  );
};

// Navigation
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinks = [
    { label: "About Us", to: "/about" },
    { label: "Services", href: "#services" },
    { label: "Careers", to: "/careers" },
    { label: "Join Us", href: "#translator" },
  ];

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (href?.startsWith('#')) {
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav 
        data-testid="navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'nav-scrolled' : 'nav-transparent'}`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="inline-flex items-center"><img src="/graphikpro-logo.png" alt="Graphikpro" className="h-8 w-auto" /></a>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
              link.to ? (
                <Link key={link.label} to={link.to} className="mono-font text-xs tracking-[0.2em] text-[#A1A1AA] hover:text-[#17B352] transition-colors uppercase">{link.label}</Link>
              ) : (
                <a key={link.label} href={link.href} className="mono-font text-xs tracking-[0.2em] text-[#A1A1AA] hover:text-[#17B352] transition-colors uppercase">{link.label}</a>
              )
            )}
            <a href="#contact" className="glow-button text-sm py-2 px-6">Contact</a>
          </div>

          {/* Mobile Hamburger */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-[#A1A1AA] hover:text-[#17B352] transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            <div className="absolute inset-0 bg-[#0B0B0D]" />
            <div className="relative h-full flex flex-col justify-between px-8 pt-24 pb-12">
              {/* Nav Links */}
              <nav className="flex flex-col gap-0">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.label}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.06 }}
                    className="border-b border-[#1a1a1c]"
                  >
                    {link.to ? (
                      <Link to={link.to} onClick={() => setMobileOpen(false)}
                        className="flex items-center justify-between py-5 group">
                        <span className="text-2xl text-[#E5E5E5] group-hover:text-[#17B352] transition-colors" style={{ fontFamily: "'Instrument Serif', serif" }}>
                          {link.label}
                        </span>
                        <span className="mono-font text-[10px] text-[#3f3f46] tracking-wider">0{i + 1}</span>
                      </Link>
                    ) : (
                      <a href={link.href} onClick={() => handleNavClick(link.href)}
                        className="flex items-center justify-between py-5 group">
                        <span className="text-2xl text-[#E5E5E5] group-hover:text-[#17B352] transition-colors" style={{ fontFamily: "'Instrument Serif', serif" }}>
                          {link.label}
                        </span>
                        <span className="mono-font text-[10px] text-[#3f3f46] tracking-wider">0{i + 1}</span>
                      </a>
                    )}
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + navLinks.length * 0.06 }}
                  className="pt-6"
                >
                  <a href="#contact" onClick={() => handleNavClick('#contact')}
                    className="glow-button inline-block text-center w-full py-4">
                    Contact
                  </a>
                </motion.div>
              </nav>

              {/* Bottom info */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mono-font text-[10px] text-[#3f3f46] tracking-wider uppercase"
              >
                <p>Media Localization</p>
                <p className="mt-1">Puducherry, India</p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Business Automation pre-footer strip — cross-sell to sister brand graphikpro.company
const BusinessAutomationStrip = () => {
  return (
    <section
      data-testid="business-automation-strip"
      className="relative border-t border-[#1f1f23] py-10 md:py-14 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ background: "radial-gradient(600px circle at 50% 50%, #17B352 0%, transparent 60%)" }} />
      <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10 relative">
        <div className="shrink-0 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#17B352]/10 border border-[#17B352]/30 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#17B352]" />
          </div>
          <p className="mono-font text-[10px] uppercase tracking-[0.3em] text-[#17B352]">
            Need Business<br />Automation?
          </p>
        </div>
        <p className="flex-1 text-[#A1A1AA] text-sm md:text-base leading-relaxed">
          Visit{" "}
          <a
            href="https://graphikpro.company"
            target="_blank"
            rel="noreferrer"
            data-testid="automation-inline-link"
            className="text-[#17B352] hover:underline decoration-2 underline-offset-2"
          >
            graphikpro.company
          </a>{" "}
          to discover how we design custom software, AI-powered workflows, and
          automation systems that help businesses operate smarter and scale faster.
        </p>
        <a
          href="https://graphikpro.company"
          target="_blank"
          rel="noreferrer"
          data-testid="automation-visit-btn"
          className="shrink-0 mono-font text-xs uppercase tracking-[0.2em] px-5 py-3 rounded-full border border-[#17B352]/40 text-[#17B352] hover:bg-[#17B352] hover:text-black transition-colors inline-flex items-center gap-2"
        >
          Explore
          <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const [showPortfolioLink, setShowPortfolioLink] = useState(true);
  useEffect(() => {
    axios.get(`${API}/portfolio/display-settings`)
      .then((r) => setShowPortfolioLink(r.data?.show_on_homepage !== false))
      .catch(() => {});
  }, []);
  return (
  <footer data-testid="footer" className="border-t border-[#1f1f23]">
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 mb-8">
        <img src="/graphikpro-logo.png" alt="Graphikpro" className="h-8 w-auto" />
        <div className="flex items-center gap-6">
          <Link to="/about" className="text-[#52525B] hover:text-[#17B352] transition-colors mono-font text-xs uppercase tracking-[0.2em]">About</Link>
          {showPortfolioLink && (
            <Link data-testid="footer-portfolio-link" to="/portfolio" className="text-[#52525B] hover:text-[#17B352] transition-colors mono-font text-xs uppercase tracking-[0.2em]">Portfolio</Link>
          )}
          <Link to="/careers" className="text-[#52525B] hover:text-[#17B352] transition-colors mono-font text-xs uppercase tracking-[0.2em]">Careers</Link>
          <a
            href="https://graphikpro.company"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-automation-link"
            className="text-[#52525B] hover:text-[#17B352] transition-colors mono-font text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1"
          >
            Automation
            <ExternalLink className="w-3 h-3" />
          </a>
          <Link to="/privacy" className="text-[#52525B] hover:text-[#17B352] transition-colors mono-font text-xs uppercase tracking-[0.2em]">Privacy</Link>
          <Link to="/terms" className="text-[#52525B] hover:text-[#17B352] transition-colors mono-font text-xs uppercase tracking-[0.2em]">Terms</Link>
        </div>
      </div>
      <div className="border-t border-[#1f1f23] pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <p className="mono-font text-[10px] text-[#3f3f46] uppercase tracking-[0.2em] mb-1">Registered Office</p>
          <p className="text-xs text-[#52525B] leading-relaxed mb-3">
            Graphikpro Private Limited, 30, Subrayan Street, Tagore Nagar, Lawspet, Puducherry - 605008
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mono-font text-xs">
            <a href="mailto:askus@graphikpro.com" className="text-[#52525B] hover:text-[#17B352] transition-colors flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> askus@graphikpro.com
            </a>
            <a href="https://wa.me/919342055964" target="_blank" rel="noreferrer" className="text-[#52525B] hover:text-[#17B352] transition-colors flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" /> +91 93420 55964
            </a>
            <a href="https://www.linkedin.com/in/madhiee/" target="_blank" rel="noreferrer" className="text-[#52525B] hover:text-[#17B352] transition-colors flex items-center gap-1.5">
              <Linkedin className="w-3.5 h-3.5" /> LinkedIn
            </a>
          </div>
        </div>
        <p className="mono-font text-xs text-[#3f3f46] tracking-[0.15em] shrink-0">
          © {new Date().getFullYear()} Graphikpro. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
  );
};

// Cinematic Splash Screen
const SplashScreen = ({ onComplete }) => {
  return (
    <motion.div
      data-testid="splash-screen"
      className="fixed inset-0 z-[9999] bg-[#0B0B0D] flex items-center justify-center"
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: "easeInOut" }}
    >
      <div className="text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-6"
        >
          <img src="/graphikpro-logo.png" alt="Graphikpro" className="h-16 md:h-20 w-auto" />
        </motion.div>
        <motion.div
          className="w-48 h-[1px] mx-auto bg-[#1f1f23] overflow-hidden rounded-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <motion.div
            className="h-full bg-[#17B352]"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 1.8, delay: 0.4, ease: "easeInOut" }}
            onAnimationComplete={onComplete}
          />
        </motion.div>
        <motion.p
          className="mono-font text-[10px] text-[#52525B] tracking-[0.3em] uppercase mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Media Localization
        </motion.p>
      </div>
    </motion.div>
  );
};

// Simple analytics tracker
const trackEvent = async (event, data = {}) => {
  try {
    await axios.post(`${API}/analytics`, { event, data, timestamp: new Date().toISOString(), url: window.location.href });
  } catch (e) { /* silent fail */ }
};

// Home Page content
const HomePage = () => {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    trackEvent('page_view', { page: 'home' });
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <AnimatePresence mode="wait">
        {showSplash && (
          <SplashScreen onComplete={() => setShowSplash(false)} />
        )}
      </AnimatePresence>

      {!showSplash && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Navigation />
          <AudioToggle />
          
          <main>
            <HeroSection 
              onWorkWithUs={() => scrollToSection('contact')}
              onJoinTranslator={() => scrollToSection('translator')}
            />
            <GlobalReachSection />
            <div id="services">
              <ServicesSection />
            </div>
            <WorkflowSection />
            <WhySection />
            <TranslatorSection id="translator" />
            <FounderSection />
            <ContactSection id="contact" />
          </main>

          <BusinessAutomationStrip />
          <Footer />
        </motion.div>
      )}
    </>
  );
};

// Main App with routing
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" theme="dark" />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/careers/:jobSlug" element={<JobDetailPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/portfolio" element={<PortfolioPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/apply/:slug" element={<ApplyPage />} />
      </Routes>
    </div>
  );
}

export default App;
